import React, { Dispatch, SetStateAction, useState } from 'react'
import { useIntl } from 'react-intl'
import { BasicButton } from '../../widgets/components/UI/BasicButton'
import { createActivityLog, renameFile, renameFolder } from '../core/_requests'
import { useAuth } from '../../auth'
import TextInput from '../../widgets/components/Input/TextInput'
import { Formik } from 'formik'
import { Col, Form, Modal } from 'react-bootstrap'
import { CustomButton } from '../../widgets/components/UI/CustomButton'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { getLocation } from '../../onboarding/core/_requests'

export const RenameFileOrFolderModal = ({
  showRenameModal,
  showRenameShowModal,
  type,
  versionId,
  currentFolderLevel,
  recordId,
  folderOrFileName,
  folderStructure,
  setFolderStructure,
  setSelectedFolders,
  setLocalExpanded,
  setSelectedPath,
  setRenameUpdate
}: {
  showRenameModal: boolean
  showRenameShowModal: Dispatch<SetStateAction<boolean>>
  type: string | undefined
  versionId: string | undefined
  recordId?: string | undefined
  currentFolderLevel?: string
  folderOrFileName: string | undefined
  folderStructure: any
  setFolderStructure: Dispatch<SetStateAction<any>>
  setSelectedFolders: Dispatch<SetStateAction<any[]>>
  setLocalExpanded: Dispatch<SetStateAction<Record<string, boolean>>>
  setSelectedPath: Dispatch<SetStateAction<string[]>>
  setRenameUpdate?: Dispatch<SetStateAction<boolean>>
}) => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const [renameLoading, setRenameLoading] = useState(false)

  const initialValues = {
    name: folderOrFileName || '',
  }

  const fileSchema = Yup.object().shape({
    name: Yup.string()
      .required(formatMessage({ id: 'Name is required' }))
      .nullable(),
  })
  const getDeviceType = () => {
    const ua = navigator.userAgent

    if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera Mobi|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
        ua
      )
    ) {
      return 'MOBILE'
    }
    return 'WEB'
  }

  const getOS = () => {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'APPLE'
    if (/Win/.test(ua)) return 'WINDOWS'
    if (/android/i.test(ua)) return 'ANDROID'
    return 'Unknown'
  }
  const createActivity = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip
    try {
      if (deviceTypeView && os && city && recordId && companyId) {
        const {
          data: { success, errors },
        } = await createActivityLog(
          companyId,
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'RENAMED'
        )
        if (!success) {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }
  const onSubmit = async (values: any) => {
    if (!companyId) return
    try {
      setRenameLoading(true)
      if (type === 'folder') {
        if (!recordId) return
        if (!currentFolderLevel) return

        const {
          data: { success, errors },
        } = await renameFolder(companyId, recordId, currentFolderLevel, { name: values.name })
        if (success) {
          showRenameShowModal(false)

          toast.success(`Folder renamed successfully`)

          if (!folderOrFileName) return

          const updatedStructure = updateFolderStructure(
            folderStructure,
            folderOrFileName,
            values.name,
            currentFolderLevel
          )

          setFolderStructure(updatedStructure)

          if (setSelectedFolders) {
            setSelectedFolders((prev: any[]) =>
              prev.map((folder: any) => {
                if (folder.name === folderOrFileName) {
                  return {
                    ...folder,
                    name: values.name,
                    activities: (parseInt(folder.activities) + 1).toString(),
                  }
                }
                return folder
              })
            )
          }

          setSelectedPath((prev: string[]) => {
            return prev.map((pathSegment) =>
              pathSegment === folderOrFileName ? values.name : pathSegment
            )
          })

          if (setLocalExpanded) {
            setLocalExpanded((prev: Record<string, boolean>) => {
              const updated = { ...prev }
              if (updated[`${folderOrFileName}-${recordId}`]) {
                updated[values.name] = updated[`${folderOrFileName}-${recordId}`]
                delete updated[`${folderOrFileName}-${recordId}`]
              }
              return updated
            })
          }
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      } else {
        if (!versionId) return
        const {
          data: { success, errors },
        } = await renameFile(companyId, versionId, { documentName: values.name })
        if (success) {
          showRenameShowModal(false)
          createActivity(recordId)
          toast.success(`File renamed successfully`)
          if (currentFolderLevel === 'LevelFive' && folderOrFileName) {
            const updatedStructure = updateFolderStructure(
              folderStructure,
              folderOrFileName,
              values.name,
              currentFolderLevel
            )

            setFolderStructure(updatedStructure)

            // Update selected folders if needed
            if (setSelectedFolders) {
              setSelectedFolders((prev: any[]) =>
                prev.map((folder: any) => {
                  if (folder.fileVersionId === versionId) {
                    return {
                      ...folder,
                      name: values.name,
                      activities: (parseInt(folder.activities) + 1).toString(),
                    }
                  }
                  return folder
                })
              )
            }

            setSelectedPath((prev: string[]) => {
              return prev.map((pathSegment) =>
                pathSegment === folderOrFileName ? values.name : pathSegment
              )
            })
          }
          if (setRenameUpdate) {
            setRenameUpdate(true); // Update state if the function is provided
          }
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setRenameLoading(false)
    }
  }

  const updateFolderStructure = (
    structure: any,
    oldName: string,
    newName: string,
    level: string,
    currentLevel: number = 1
  ): any => {
    // Handle Level One directly
    if (level === 'LevelOne' && currentLevel === 1) {
      if (structure.hasOwnProperty(oldName)) {
        const entries = Object.entries(structure)
        const index = entries.findIndex(([key]) => key === oldName)
        if (index !== -1) {
          entries[index] = [newName, structure[oldName]]
          return Object.fromEntries(entries)
        }
      }
      return structure
    }

    // Create a copy of the structure
    const updatedStructure = { ...structure }

    // Recursive function to update nested structure
    const updateNestedStructure = (obj: any, depth: number): any => {
      if (!obj || typeof obj !== 'object') return obj

      // Handle current level
      if (obj.children) {
        // Convert children to entries array to maintain order
        const entries = Object.entries(obj.children)
        const index = entries.findIndex(([key]) => key === oldName)

        // Check if the folder to rename exists at this level
        if (index !== -1) {
          const shouldUpdateAtThisLevel =
            (level === 'LevelTwo' && depth === 1) ||
            (level === 'LevelThree' && depth === 2) ||
            (level === 'LevelFour' && depth === 3) ||
            (level === 'LevelFive' && depth === 4) // Added LevelFive condition

          if (shouldUpdateAtThisLevel) {
            // Update the name while maintaining position
            entries[index] = [newName, entries[index][1]]
            obj.children = Object.fromEntries(entries)
            return obj
          }
        }

        // Recursively update children
        entries.forEach(([key, value], idx) => {
          entries[idx] = [key, updateNestedStructure(value, depth + 1)]
        })

        obj.children = Object.fromEntries(entries)
      }

      return obj
    }

    // Process each top-level folder
    const entries = Object.entries(updatedStructure)
    entries.forEach(([key, value], index) => {
      entries[index] = [key, updateNestedStructure(value, 1)]
    })

    return Object.fromEntries(entries)
  }

  return (
    <Modal
      show={showRenameModal}
      onHide={() => showRenameShowModal(false)}
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header closeButton>
        <h2>{type === 'folder' ? 'Rename Folder' : 'Rename File'}</h2>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={fileSchema}
        onSubmit={onSubmit}
      >
        {(formik) => {
          return (
            <Form>
              <Modal.Body>
                <Col md={12}>
                  <TextInput
                    fieldType='text'
                    label={
                      type === 'folder'
                        ? formatMessage({ id: 'Folder name' })
                        : formatMessage({ id: 'File name' })
                    }
                    fieldName='name'
                    formik={formik}
                    placeholder={
                      type === 'folder'
                        ? formatMessage({
                          id: 'Enter Folder name',
                        })
                        : formatMessage({
                          id: 'Enter File name',
                        })
                    }
                    isStarRequired={true}
                    isTooltipNotRequired={true}
                  />
                </Col>
              </Modal.Body>
              <Modal.Footer>
                <div className='d-flex flex-wrap gap-3'>
                  <BasicButton
                    buttonText={formatMessage({ id: 'Cancel' })}
                    border='none'
                    color='#F5F8FA'
                    textColor='#5E6278'
                    minWidth={90}
                    height='44px'
                    onClick={() => {
                      formik.resetForm()
                      showRenameShowModal(false)
                    }}
                  />

                  <CustomButton
                    isSubmitting={formik.isSubmitting}
                    isValid={formik.isValid}
                    buttonText={'Save'}
                    loading={renameLoading}
                    height={44}
                    widthLoading={1}
                    width={1}
                    margin={'me-0'}
                    onSubmit={formik.handleSubmit}
                    marginButtom='0'
                  />
                </div>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
